import axios from "axios";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import UserPool from "../services/aws/UserPool";
import { queueGetAll, queueDelete, queuePut, queueCount } from "./OfflineQueue";
import { dataURLtoFile } from "./saveImageToS3";

interface DrainCallbacks {
  onCountChange: (count: number) => void;
  onSyncedItems: (count: number) => void;
  onOfflineFailure: (count: number) => void;
  onItemSynced?: (itemId: string, s3ImageUrl: string | undefined, payload: any) => void;
  onItemsDeleted?: (itemIds: string[]) => void;
}

let callbacks: DrainCallbacks | null = null;
let triggersActive = false;
let drainInterval: ReturnType<typeof setInterval> | null = null;

// Streak state — in-memory only, resets on queue empty or every 15 min
let hasShownPopupThisStreak = false;
let streakResetTimer: ReturnType<typeof setTimeout> | null = null;
const STREAK_RESET_MS = 15 * 60 * 1000;

function resetStreak(): void {
  hasShownPopupThisStreak = false;
  if (streakResetTimer) {
    clearTimeout(streakResetTimer);
    streakResetTimer = null;
  }
}

function startStreakTimer(): void {
  if (streakResetTimer) return;
  streakResetTimer = setTimeout(async () => {
    hasShownPopupThisStreak = false;
    streakResetTimer = null;
    const count = await queueCount();
    if (count > 0) {
      hasShownPopupThisStreak = true;
      startStreakTimer();
      callbacks?.onOfflineFailure(count);
    }
  }, STREAK_RESET_MS);
}

// Direct Cognito session call — no React context needed, safe for event listeners.
// Exported so other feature drains (src/features/lists) reuse it rather than
// reimplementing the session-expired dispatch.
export function getTokenForDrain(): Promise<string> {
  return new Promise((resolve, reject) => {
    const user = UserPool.getCurrentUser();
    if (!user) { reject(new Error("No authenticated user")); return; }
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        if ((err as any)?.code === "NotAuthorizedException") {
          window.dispatchEvent(new CustomEvent("cognitoSessionExpired"));
        }
        reject(err ?? new Error("Session expired"));
      } else {
        resolve(session.getIdToken().getJwtToken());
      }
    });
  });
}

function isNetworkError(err: any): boolean {
  if (axios.isAxiosError(err)) {
    return !err.response || err.code === "ECONNABORTED" || err.code === "ERR_NETWORK";
  }
  return false;
}

// Retry helper: attempts fn up to delays.length+1 times with specified inter-attempt delays.
// isRetryable lets callers skip retries for non-network errors (e.g. 401, 599).
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  delays: number[] = [1500, 3000],
  isRetryable: (err: any) => boolean = () => true
): Promise<T> {
  let lastError: any;
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (attempt < delays.length && isRetryable(err)) {
        await new Promise(r => setTimeout(r, delays[attempt]));
      } else {
        break;
      }
    }
  }
  throw lastError;
}

// Called from SaveItem after a failed save that gets queued.
// Shows the failure popup at most once per 15-minute streak window.
export function notifyOfflineFailure(queueSize: number): void {
  if (!hasShownPopupThisStreak) {
    hasShownPopupThisStreak = true;
    startStreakTimer();
    callbacks?.onOfflineFailure(queueSize);
  }
  callbacks?.onCountChange(queueSize);
}

export async function drainQueue(): Promise<void> {
  if (!navigator.onLine) return;
  // Draining is paused for the whole impersonation session: a background drain here would
  // apply whichever user's queued item happens to sync to the currently-mounted itemMap
  // (drain callbacks don't check item ownership against the active user), splicing one
  // account's data into another's view. Resumes automatically once impersonation ends
  // (reload clears localStorage and this check passes again).
  if (localStorage.getItem("impersonatedUserId")) return;

  let token: string;
  try {
    token = await getTokenForDrain();
  } catch {
    return; // Refresh token expired — let the app's auth flow handle it
  }

  const items = await queueGetAll();
  if (items.length === 0) return;

  let syncedCount = 0;

  for (const item of items) {
    try {
      if (item.method === "DELETE") {
        await axios({
          method: "delete",
          url: process.env.REACT_APP_AWS_ITEM_API_URL!,
          data: { items: item.itemIds },
          headers: {
            Authtoken: token,
            "Access-Control-Allow-Headers": "*",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "*",
            "Content-Type": "application/json",
            "x-data-user-id": item.userId,
          },
        });
        await queueDelete(item.localId);
        callbacks?.onItemsDeleted?.(item.itemIds ?? []);
      } else {
        // Replace the UUID at the end of the Image URL before every POST. The backend uses
        // an unchanged URL as a signal that the image wasn't replaced and skips presigned
        // URL generation. A fresh UUID guarantees a new presigned URL each attempt.
        // Persisting the updated payload back to the queue keeps the queue and React state
        // in sync if this attempt fails and the item is retried.
        if (item.thumbnailDataUrl && item.payload.Image) {
          const lastSlash = item.payload.Image.lastIndexOf("/");
          if (lastSlash !== -1) {
            item.payload = {
              ...item.payload,
              Image: item.payload.Image.substring(0, lastSlash + 1) + crypto.randomUUID(),
            };
            await queuePut(item);
          }
        }

        // Always re-POST to get fresh presigned S3 URLs — never persist presigned URLs
        const res = await axios.post(
          process.env.REACT_APP_AWS_ITEM_API_URL!,
          item.payload,
          {
            headers: {
              Authtoken: token,
              "Access-Control-Allow-Headers": "*",
              "Access-Control-Allow-Origin": "*",
              "Access-Control-Allow-Methods": "*",
              "Content-Type": "application/json",
              "x-data-user-id": item.userId,
            },
          }
        );

        if (item.thumbnailDataUrl && res.data.mainImageUrl) {
          const img = dataURLtoFile(
            item.thumbnailDataUrl,
            `user_images/${item.userId}/${item.localId}_mainimg.jpeg`
          );
          if (img) {
            await axios.put(res.data.mainImageUrl, img, {
              headers: { "Content-Type": "image/jpeg", "Cache-Control": "max-age=31536000" },
              timeout: 10000,
            });
          }
        }
        // Large image is intentionally NOT retried here — dropped at save-time per spec

        await queueDelete(item.localId);
        // item.payload.Image may have been updated above with a new UUID — use it here so
        // React state reflects the URL that was actually written to DynamoDB.
        const s3ImageUrl = item.payload.Image?.replace("{CLIENTID}", item.userId);
        callbacks?.onItemSynced?.(item.localId, s3ImageUrl, item.payload);
      }
      syncedCount++;
    } catch (err) {
      if (isNetworkError(err)) break; // Still offline — stop, leave remainder queued
      item.attempts++;
      await queuePut(item); // Persist incremented attempt count
    }
  }

  const remaining = await queueCount();
  callbacks?.onCountChange(remaining);

  if (syncedCount > 0) {
    if (remaining === 0) resetStreak();
    callbacks?.onSyncedItems(syncedCount);
  }
}

function onVisibilityChange(): void {
  if (!document.hidden) drainQueue();
}

export function setupDrainTriggers(cbs: DrainCallbacks): void {
  callbacks = cbs;
  if (triggersActive) return;
  triggersActive = true;
  window.addEventListener("online", drainQueue);
  document.addEventListener("visibilitychange", onVisibilityChange);
  drainInterval = setInterval(drainQueue, 45000);
}

export function teardownDrainTriggers(): void {
  window.removeEventListener("online", drainQueue);
  document.removeEventListener("visibilitychange", onVisibilityChange);
  if (drainInterval) { clearInterval(drainInterval); drainInterval = null; }
  triggersActive = false;
  callbacks = null;
  resetStreak();
}
